// Turns the AI field suggestions a user ticked into the API calls that apply
// them to a template. Pure, so it is unit-tested (applySuggestionPlan.test.js).
//
// Every suggestion carries a placement from the backend
// (src/services/schema_designer.py):
//   new_field   -> create a header field
//   new_table   -> create a table field with its columns
//   add_columns -> append columns to a table the template already has (the
//                  server appends them to the stored columns, so nothing the
//                  table already has - NetSuite mappings included - is lost)
//   exists      -> already in the template; nothing to do

const COLUMN_KEYS = ["key", "label", "type", "description"];

export function suggestionKey(item) {
  const action = item?.placement?.action || "new_field";
  return `${action}:${item?.field_name || item?._manualId || ""}`;
}

export function isOptionalSuggestion(item) {
  return item?.recommended === false && !item?.requested;
}

// The columns of a table suggestion that are ticked, cleaned for the API.
export function selectedColumns(item) {
  return (item?.columns || [])
    .filter((c) => c && c.selected !== false && (c.key || c.label))
    .map((c) => {
      const col = {};
      COLUMN_KEYS.forEach((k) => {
        if (c[k] !== undefined && c[k] !== null && c[k] !== "") col[k] = c[k];
      });
      if (!col.key) col.key = String(c.label).toLowerCase().replace(/[^a-z0-9]+/g, "_");
      if (!col.label) col.label = col.key;
      if (!col.type) col.type = "text";
      col.required = false;
      return col;
    });
}

export function planSuggestionChanges(items, existingFields = []) {
  const existingByName = new Map(
    (existingFields || []).map((f) => [String(f.field_name || "").toLowerCase(), f]),
  );
  const creates = [];
  const appends = [];
  const skipped = [];
  const takenNames = new Set(existingByName.keys());

  for (const item of items || []) {
    const action = item?.placement?.action || "new_field";
    const name = item?.field_name;
    if (!name) continue;

    if (action === "exists") {
      skipped.push({ field_name: name, reason: "already in the template" });
      continue;
    }

    if (action === "add_columns") {
      const target =
        (existingFields || []).find((f) => String(f.id) === String(item.placement?.target_field_id)) ||
        existingByName.get(String(item.placement?.target_field_name || name).toLowerCase());
      const columns = selectedColumns(item);
      if (!columns.length) {
        skipped.push({ field_name: name, reason: "no columns selected" });
        continue;
      }
      if (target) {
        appends.push({ field_id: target.id, field_name: target.field_name, columns });
        continue;
      }
      // The table was removed since the suggestion was made: create it.
    }

    if (takenNames.has(String(name).toLowerCase())) {
      skipped.push({ field_name: name, reason: "a field with this name already exists" });
      continue;
    }

    const isTable = item.field_type === "array_of_objects" || action === "add_columns";
    const payload = {
      field_name: name,
      display_name: item.display_name || name,
      field_type: isTable ? "array_of_objects" : item.field_type || "text",
      category: item.category || "",
      description: item.description || "",
    };
    if (item.ns_key) payload.ns_field = item.ns_key;
    if (item.normalisation_instruction) payload.normalisation_instruction = item.normalisation_instruction;
    if (isTable) {
      const columns = selectedColumns(item);
      if (!columns.length) {
        skipped.push({ field_name: name, reason: "no columns selected" });
        continue;
      }
      payload.columns = columns;
      payload.field_values = JSON.stringify(columns);
    }
    takenNames.add(String(name).toLowerCase());
    creates.push({ field_name: name, payload });
  }
  return { creates, appends, skipped };
}

// The wizard creates its template through import-fields, which takes a
// {field_name: definition} map; tables travel as a `columns` list.
export function suggestionsToImportMap(items) {
  const out = {};
  for (const item of items || []) {
    if (!item?.field_name || item?.placement?.action === "exists") continue;
    const isTable = item.field_type === "array_of_objects";
    const def = {
      label: item.display_name || item.field_name,
      type: isTable ? "array_of_objects" : item.field_type || "text",
      description: item.description || "",
      category: item.category || undefined,
    };
    if (isTable) {
      def.columns = selectedColumns(item);
      if (!def.columns.length) continue;
    }
    out[item.field_name] = def;
  }
  return out;
}
